/**
 * Aufbau der Grid-Spalten aus den Ansichtsfeldern — Port von
 * `IwmBasePageView.GetColumns` und `IwmDataGridBase.GetColumnHeader`.
 */

import { findTemplate } from '@/components/columns/templateLookup';
import type { CellTemplateMap } from '@/components/columns/types';
import { getCaption } from '@/types/captions';
import type { ChinaAppSysViewField } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';
import { getFieldValueType } from '@/types/fieldTypes';

import type { GridColumn } from './types';

export interface BuildColumnsOptions<T> {
  fields: ChinaAppSysViewField[];
  entity: EntityTypes;
  cellTemplates: CellTemplateMap<T>;
  editTemplates: CellTemplateMap<T>;
}

/**
 * Beschriftungsrangfolge aus `GetColumnHeader`: `FieldCaption`, dann das `[Caption]`-Attribut,
 * sonst der Feldname. Die Sprachumschaltung ist im Altclient auskommentiert und entfällt.
 */
function getColumnHeader(entity: EntityTypes, field: ChinaAppSysViewField): string {
  const caption = field.FieldCaption;
  if (caption !== null && caption !== '') return caption;
  return getCaption(entity, field.FieldName ?? '');
}

/** Spaltenreihenfolge ist `sortIndex` aufsteigend (`Columns.OrderBy(e => e.SortIndex)`). */
export function buildColumns<T>(options: BuildColumnsOptions<T>): GridColumn<T>[] {
  return options.fields
    .map((field): GridColumn<T> => {
      const fieldName = field.FieldName ?? '';
      return {
        field: fieldName,
        headerText: getColumnHeader(options.entity, field),
        visible: field.Visible,
        allowEditing: !field.ReadOnly,
        width: field.SpaltenBreite,
        isPrimaryKey: fieldName.toLowerCase() === 'id',
        sortIndex: field.Sortierung,
        fixiert: field.Fixiert,
        valueType: getFieldValueType(options.entity, fieldName),
        cellTemplate: findTemplate(options.cellTemplates, fieldName),
        editTemplate: findTemplate(options.editTemplates, fieldName),
      };
    })
    .sort((left, right) => left.sortIndex - right.sortIndex);
}
